import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Admin auth now rides on an HttpOnly, server-signed `mattoo_admin_session`
// cookie set by POST /admin/login. The browser attaches it automatically on
// same-origin requests when `withCredentials` is on, so we no longer need to
// read/write the raw PIN from sessionStorage.
//
// `AUTH_HINT_KEY` is a non-sensitive boolean flag that lets the Admin UI
// render the authed view immediately on mount instead of flashing the login
// gate. The server is still the source of truth — any 401 clears the hint.
const AUTH_HINT_KEY = "mattoo_admin_authed";

export const isAdminAuthed = () =>
  sessionStorage.getItem(AUTH_HINT_KEY) === "1";

const _setAuthHint = (on) => {
  if (on) sessionStorage.setItem(AUTH_HINT_KEY, "1");
  else sessionStorage.removeItem(AUTH_HINT_KEY);
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Any 401 on an admin route = session expired. Clear the local hint so the
// Admin page bounces the user back to the login gate on next render.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (
      err?.response?.status === 401 &&
      (err?.config?.url || "").includes("/admin/")
    ) {
      _setAuthHint(false);
    }
    return Promise.reject(err);
  },
);

export const adminLogin = async (pin) => {
  const res = await api.post("/admin/login", { pin });
  if (res.data?.ok) _setAuthHint(true);
  return res.data;
};

export const adminLogout = async () => {
  try {
    await api.post("/admin/logout");
  } catch {
    // Best-effort — clear the hint regardless of server reply.
  }
  _setAuthHint(false);
};

export const adminMe = () => api.get("/admin/me").then((r) => r.data);

export const getSkills = () => api.get("/skills").then((r) => r.data);
export const getFields = () => api.get("/fields").then((r) => r.data);
export const getInterests = () => api.get("/interests").then((r) => r.data);
export const getRoles = () => api.get("/roles").then((r) => r.data);
export const getPreviousRoles = () => api.get("/previous-roles").then((r) => r.data);
export const postRecommend = (payload) => api.post("/recommend", payload).then((r) => r.data);
export const getStats = () => api.get("/responses/stats").then((r) => r.data);
export const submitLead = (payload) =>
  api.post("/leads", payload).then((r) => r.data);

export const uploadRolesCsv = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/admin/roles-import", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

const _downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const downloadRolesCsv = async () => {
  const res = await api.get("/admin/roles-export.csv", {
    responseType: "blob",
  });
  _downloadBlob(res.data, "mattoo-rolefinder-roles.csv");
};

export const downloadMethodologyMd = async () => {
  const res = await api.get("/admin/methodology.md", {
    responseType: "blob",
  });
  _downloadBlob(res.data, "mattoo-rolefinder-methodology.md");
};

export const getMethodologyMd = async () => {
  const res = await api.get("/admin/methodology.md", { responseType: "text" });
  return res.data;
};
